import { WallType } from "building-types/walls/wallEnums";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallTable } from "building-types/walls/wallTableTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { Face } from "geom/brep";
import { faceCopy, faceNew } from "geom/brep";
import { ZERO_EPSILON } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import { planeNewXY } from "geom/plane";
import { polyLine2Area, polyLine2ReflectOverYAxis, polyLine2Reverse, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { solidWallCrossSection } from "./solidWallCrossSection";
import { getWallPath } from "./wall";
import { wallMassCrossSectionLayers } from "./wallLayers";

export function wallsCrossSectionFaces(
  planarGraph: PlanarGraph,
  walls: WallSystem,
  wallCutZ: number,
  wallCutLayer: WallLayerType,
  reverseFaces: boolean,
  includeGaps: boolean,
  includeStairClips = false,
  includeMiterGaps = true,
  /**
   * Optional predicate limiting which walls have their cross-section bodies emitted. When
   * provided, walls failing the predicate are not drawn, but they are still considered when
   * filling miter gaps so that corners shared with an included wall remain complete. A miter
   * filler face is only emitted when at least one wall at the vertex is included. When omitted,
   * every wall is included (the default behavior).
   */
  shouldIncludeWall?: (wall: Wall) => boolean,
): Face[] {
  const wallLOD = WallLOD.L2;
  const wallPlane = planeNewXY(0, 0, wallCutZ - 0.001);
  const wallFaces: Face[] = [];
  function addWalls(wallTable: WallTable) {
    for (const wallId in wallTable) {
      const wall = walls.walls[wallId];
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }
      if (shouldIncludeWall && !shouldIncludeWall(wall)) {
        continue;
      }
      // wall cut plane is relative to the floor elevation, so we need to convert this
      // TODO - covert to LevelNumber
      const wallCutPlane = wallCutZ - wall.mass.floorElevation;
      if (wall.trimmedByStairs) {
        for (const face of wall.wallFaces.topFaces) {
          const copy = faceCopy(face);
          wallFaces.push(faceNew(copy.loops, wallPlane));
        }

        if (includeStairClips) {
          const wallCrossSection = solidWallCrossSection({
            wall,
            includeDoors: includeGaps,
            includeWindows: includeGaps,
            includeWallCuts: includeGaps,
            closedPolyLines: true,
            wallLayer: wallCutLayer,
            wallLOD,
            cutPlane: wallCutPlane,
          });
          for (const line of wallCrossSection) {
            wallFaces.push(faceNew([polyLine2SetCW(line)], wallPlane));
          }
        }

        continue;
      }

      const wallCrossSection = solidWallCrossSection({
        wall,
        includeDoors: includeGaps,
        includeWindows: includeGaps,
        includeWallCuts: includeGaps,
        closedPolyLines: true,
        wallLayer: wallCutLayer,
        wallLOD,
        cutPlane: wallCutPlane,
      });

      for (const line of wallCrossSection) {
        wallFaces.push(faceNew([polyLine2SetCW(line)], wallPlane));
      }
    }
  }

  addWalls(walls.walls);

  if (includeMiterGaps) {
    // solidWallCrossSection leaves some gaps, fill them in here.
    for (let vIndex = 0, vCount = planarGraph.vertexHalfEdges.length; vIndex < vCount; vIndex++) {
      const vertexHalfEdges = planarGraph.vertexHalfEdges[vIndex];

      const fillerPoints: number[] = [];
      let includedWallAtVertex = false;

      for (const vertexHalfEdge of vertexHalfEdges) {
        const wallId = walls.halfEdgeToWall[vertexHalfEdge];
        if (!wallId) {
          continue;
        }
        const wall = walls.walls[wallId];
        if (!wall) {
          continue;
        }

        if (wall.wallType === WallType.SOLID && wall.trimmedByStairs && !includeStairClips) {
          continue;
        }

        const wallMass = wall.wallType === WallType.SOLID ? wall.mass : wall;
        const flipped = getWallPath(wall).halfEdgeIndex !== vertexHalfEdge;

        // wall cut plane is relative to the floor elevation, so we need to convert this
        // TODO - covert to LevelNumber
        const wallCutPlane = wallCutZ - wallMass.floorElevation;
        const wallCrossSections = wallMassCrossSectionLayers(wallMass, wallCutLayer, wallLOD, wallCutPlane);

        const wallCrossSection = flipped ? wallCrossSections[wallCrossSections.length - 1] : wallCrossSections[0];
        if (!wallCrossSection || wallCrossSection.wallType === WallType.VOID) {
          continue;
        }

        const { leftEdge, rightEdge } = wallCrossSection;
        const edge = flipped ? leftEdge : rightEdge;

        if (!shouldIncludeWall || shouldIncludeWall(wall)) {
          includedWallAtVertex = true;
        }

        // TODO - @luisjaggy enable this and complete filler points logic
        fillerPoints.push(edge[0], edge[1]);
      }

      // we only need to draw a filler face if there are more than 2 walls on the vertex, and (when
      // a wall filter is active) at least one of those walls is included in the output.
      if (fillerPoints.length >= 6 && (!shouldIncludeWall || includedWallAtVertex)) {
        fillerPoints.push(fillerPoints[0]);
        fillerPoints.push(fillerPoints[1]);

        const filler = polyLine2New(fillerPoints);
        const fillerArea = polyLine2Area(filler);

        if (fillerArea < ZERO_EPSILON) {
          continue;
        }

        wallFaces.push(faceNew([polyLine2SetCW(filler)], wallPlane));
      }
    }
  }

  if (reverseFaces) {
    // Note that we can't use faceReverse helper here since the faces share the same plane.
    for (const face of wallFaces) {
      const { loops } = face;
      for (const loop of loops) {
        polyLine2Reverse(polyLine2ReflectOverYAxis(loop));
      }
    }
    wallPlane.uX *= -1;
    wallPlane.uY *= -1;
    wallPlane.uZ *= -1;
  }

  return wallFaces;
}
